import { useComplexState } from 'hook/helper.hook';
import { unformatIncome } from 'helpers/money.helper';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { commafy } from 'helpers/number';
import { AUTO_LOAN_VERTICAL } from 'types/marketplaceTiles.constants';
import { unFormatCurrency } from 'helpers/money.helper';
import {
  sortList,
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_DOWN_PAYMENT,
  MIN_NEW_AUTOLOAN,
  MAX_NEW_AUTOLOAN,
} from 'helpers/autoLoan.helper';
import { getAutoloanClickTrackingAttributes } from 'helpers/tracking.helpers';

export const useAutoLoanMarketplace = (
  creditInfo,
  userInfo,
  eventHandler,
  setCommonStates,
  getAutoLoans,
  module,
) => {
  const [state, setState] = useComplexState({
    currentAutoLoanAmount: null,
    newAutoLoanAmount: DEFAULT_LOAN_AMOUNT,
    newAutoLoanDownPayment: DEFAULT_DOWN_PAYMENT,
    currentSortType: sortList[0],
    filterTags: null,
  });

  const setCurrentSortType = e => {
    setState({
      currentSortType: e,
    });
  };

  const setNewAutoLoanAmount = newAutoLoanAmount =>
    setState({ newAutoLoanAmount });

  const onNewAutoLoanDownPaymentUpdate = e => {
    const value = unFormatCurrency(e.target.value);
    if (value === '' || (/^[0-9]+$/i.test(value) && value.length < 9)) {
      setState({
        newAutoLoanDownPayment: unFormatCurrency(e.target.value),
      });
    }
  };

  const onApplyFilter = () => {
    const tags = {};

    if (state.newAutoLoanAmount !== DEFAULT_LOAN_AMOUNT) {
      tags.loanAmount = {
        value: state.newAutoLoanAmount,
        label: `Loan $${commafy(state.newAutoLoanAmount)}`,
      };
    }

    if (
      state.newAutoLoanDownPayment !== DEFAULT_DOWN_PAYMENT &&
      state.newAutoLoanDownPayment !== '0'
    ) {
      tags.downPayment = {
        value: unformatIncome(state.newAutoLoanDownPayment),
        label: `Down Payment $${commafy(state.newAutoLoanDownPayment)}`,
      };
    }

    if (state.currentSortType !== sortList[0]) {
      tags.sortBy = {
        value: state.currentSortType,
        label: `Sort: ${state.currentSortType}`,
      };
    }

    setState({ filterTags: tags });
    setCommonStates({
      showModal: false,
    });
    getAutoLoans({
      currentSortType: state.currentSortType,
      loanAmount:
        state.newAutoLoanAmount - unformatIncome(state.newAutoLoanDownPayment),
    });
  };

  const onRemoveTagFilter = key => {
    const defaultValue = {
      loanAmount: DEFAULT_LOAN_AMOUNT,
      downPayment: DEFAULT_DOWN_PAYMENT,
      sortBy: sortList[0],
    };

    let newFilterValues = {
      loanAmount: state.newAutoLoanAmount,
      downPayment: state.newAutoLoanDownPayment,
      sortBy: state.currentSortType,
    };

    let updatedKey = { [key]: { value: defaultValue[key], label: null } };

    switch (key) {
      case 'loanAmount':
        updatedKey = {
          ...updatedKey,
          downPayment: { value: defaultValue.downPayment, label: null },
        };
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
          newAutoLoanAmount: DEFAULT_LOAN_AMOUNT,
          newAutoLoanDownPayment: DEFAULT_DOWN_PAYMENT,
        });
        newFilterValues.loanAmount = DEFAULT_LOAN_AMOUNT;
        newFilterValues.downPayment = DEFAULT_DOWN_PAYMENT;
        break;
      case 'downPayment':
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
          newAutoLoanDownPayment: DEFAULT_DOWN_PAYMENT,
        });
        newFilterValues.downPayment = DEFAULT_DOWN_PAYMENT;
        break;
      case 'sortBy':
        setState({
          filterTags: {
            ...state.filterTags,
            ...updatedKey,
          },
          currentSortType: sortList[0],
        });
        newFilterValues.sortBy = sortList[0];
        break;
      default:
        break;
    }

    getAutoLoans({
      currentSortType: newFilterValues.sortBy,
      loanAmount:
        newFilterValues.loanAmount -
        unformatIncome(newFilterValues.downPayment),
    });
  };

  const onResetFilter = () => {
    setState({
      currentAutoLoanAmount: null,
      newAutoLoanAmount: DEFAULT_LOAN_AMOUNT,
      newAutoLoanDownPayment: DEFAULT_DOWN_PAYMENT,
      currentSortType: sortList[0],
    });

    const trackingAttributes = getAutoloanClickTrackingAttributes({
      clickType: 'Reset Search Criteria',
      vertical: AUTO_LOAN_VERTICAL,
      module,
    });

    eventHandler(CLICK, trackingAttributes);
  };

  return {
    state,
    sortList,
    MIN_NEW_AUTOLOAN,
    MAX_NEW_AUTOLOAN,
    setNewAutoLoanAmount,
    onNewAutoLoanDownPaymentUpdate,
    onApplyFilter,
    onResetFilter,
    onRemoveTagFilter,
    setCurrentSortType,
    isApplyFilterDisabled:
      unformatIncome(state.newAutoLoanDownPayment) > state.newAutoLoanAmount,
  };
};
